import { ApiProblemError, sessionKeys, type ShareCodePreview } from '@taler/api-client'
import { useQueryClient } from '@tanstack/vue-query'
import { ref, type Ref } from 'vue'
import { useApi } from '@/app/api'

export type ShareCodeFailure = 'not-found' | 'rate-limited' | 'failed'

/** Unknown and archived codes share one not-found, so the copy never reveals which it was. Any 429 is the hourly budget. */
export function shareCodeFailure(error: unknown): ShareCodeFailure {
  if (error instanceof ApiProblemError && error.code === 'ledger.share_code_not_found') return 'not-found'
  if (error instanceof ApiProblemError && error.status === 429) return 'rate-limited'
  return 'failed'
}

export interface UseLedgerCode {
  readonly isRotating: Ref<boolean>
  readonly rotateError: Ref<boolean>
  lookup(code: string): Promise<ShareCodePreview>
  rotate(ledgerId: string): Promise<string>
}

export function useLedgerCode(): UseLedgerCode {
  const api = useApi()
  const queryClient = useQueryClient()
  const isRotating = ref(false)
  const rotateError = ref(false)

  return {
    isRotating,
    rotateError,
    lookup: (code) => api.relationships.previewShareCode(code),
    async rotate(ledgerId) {
      isRotating.value = true
      rotateError.value = false
      try {
        const code = await api.relationships.rotateShareCode(ledgerId)
        await queryClient.invalidateQueries({ queryKey: sessionKeys.ledgers() })
        return code
      } catch (caught) {
        rotateError.value = true
        throw caught
      } finally {
        isRotating.value = false
      }
    },
  }
}
